/**
 * Mandanten-Layer: die Kommune dieser Instanz.
 *
 * Name, Kurzname, Hausfarbe, Wappen, Kontakt und Gebiet (Gemeinden mit amtlicher Grenze aus
 * VG250) pflegt die Kommune unter Verwaltung › Gliederung. Der Stand kommt beim App-Start vom Katalog
 * (`/api/catalog/tenant-public`, in der oeffentlichen Ansicht `/twin-public/tenant`) und wird auf
 * `<html>` angewendet (`--brand-hue` / `--brand-chroma`). Ohne Pflege gilt eine neutrale
 * Darstellung ohne Gebiet; es gibt keine eingebaute Kommune.
 */
import { useSyncExternalStore } from 'react';

export interface TenantGebiet {
  /** Amtlicher Gemeindeschluessel (8 Ziffern). */
  ags: string;
  name: string;
  /** „Gemeinde", „Stadt", „Markt" … */
  bez: string;
  /** [west, sued, ost, nord] in WGS84. */
  bbox: [number, number, number, number];
  center: { lon: number; lat: number };
  geometry: { type: 'Polygon' | 'MultiPolygon'; coordinates: unknown };
}

export interface TenantConfig {
  /** Vollstaendiger Anzeigename, z. B. „Gemeinde Musterdorf". */
  name: string;
  /** Kurzform fuer Sidebar und Topbar. */
  shortName: string;
  /** Wappen (data:-URL) oder null. */
  wappenUrl: string | null;
  /** Logo (Wortmarke, data:-URL) fuer helle Flaechen; steht oben links statt Wappen und Name. */
  logoUrl: string | null;
  /** Helle Ausfuehrung des Logos fuer dunkle Flaechen (oeffentliche Seiten ueber dem Titelbild). */
  logoHellUrl: string | null;
  /** Bewegter Titelbereich der oeffentlichen Seiten (Verwaltung › Erscheinungsbild). */
  heroAnimation: boolean;
  /** Titelbild der oeffentlichen Seite und der Anmeldung (URL mit Version) oder null. */
  heroUrl: string | null;
  /** Bildnachweis zum Titelbild. */
  heroCredit: string | null;
  /** OKLCH-Hue 0..360. */
  brandHue: number;
  /** OKLCH-Chroma 0..0.4. */
  brandChroma: number;
  publisherEmail?: string;
  publisherHomepage?: string;
  /** Impressum und Datenschutzerklaerung der Kommune (Footer der oeffentlichen Seiten). */
  imprintUrl: string | null;
  privacyUrl: string | null;
  /** Webseiten, die die oeffentliche Zwillingsansicht einbetten duerfen (zusaetzlich zur eigenen). */
  embedOrigins: string[];
  /** Zusatzmodule der Kommune (Verwaltung › System & Betrieb). */
  modules: { lorawan: boolean };
  /** Gemeinden des Gebiets; leer, solange keine gepflegt ist. */
  gebiete: TenantGebiet[];
  /** Quellenvermerk der Grenzen (BKG), wenn Gebiete gepflegt sind. */
  gebieteAttribution: string | null;
  /** true, sobald der Stand vom Katalog kam (false: neutrale Darstellung, Katalog nicht erreichbar). */
  loaded: boolean;
  /** true, wenn die Kommune ihren Namen gepflegt hat (sonst steht „CiviPort“ da). */
  named: boolean;
}

export const NEUTRAL_TENANT: TenantConfig = {
  name: 'CiviPort',
  shortName: 'CiviPort',
  wappenUrl: null,
  logoUrl: null,
  logoHellUrl: null,
  heroAnimation: false,
  heroUrl: null,
  heroCredit: null,
  imprintUrl: null,
  privacyUrl: null,
  brandHue: 250,
  brandChroma: 0.13,
  embedOrigins: [],
  modules: { lorawan: false },
  gebiete: [],
  gebieteAttribution: null,
  loaded: false,
  named: false,
};

/** Antwort von /tenant-public. */
export interface TenantApi {
  name: string | null;
  short_name: string | null;
  homepage: string | null;
  email: string | null;
  brand_hue: number;
  brand_chroma: number;
  wappen: string | null;
  logo?: string | null;
  logo_hell?: string | null;
  hero_animation?: boolean;
  hero?: { credit: string | null; version: string } | null;
  embed_origins?: string[];
  modules?: { lorawan?: boolean };
  imprint_url?: string | null;
  privacy_url?: string | null;
  gebiete: TenantGebiet[];
  gebiete_attribution: string | null;
}

export function tenantFromApi(raw: TenantApi): TenantConfig {
  const name = raw.name?.trim() || NEUTRAL_TENANT.name;
  return {
    name,
    shortName: raw.short_name?.trim() || name,
    wappenUrl: raw.wappen ?? null,
    logoUrl: raw.logo ?? null,
    logoHellUrl: raw.logo_hell ?? null,
    heroAnimation: raw.hero_animation === true,
    heroUrl: raw.hero ? `/oeffentlich/hero?v=${encodeURIComponent(raw.hero.version)}` : null,
    heroCredit: raw.hero?.credit ?? null,
    brandHue: Number.isFinite(raw.brand_hue) ? raw.brand_hue : NEUTRAL_TENANT.brandHue,
    brandChroma: Number.isFinite(raw.brand_chroma) ? raw.brand_chroma : NEUTRAL_TENANT.brandChroma,
    publisherEmail: raw.email ?? undefined,
    publisherHomepage: raw.homepage ?? undefined,
    imprintUrl: raw.imprint_url ?? null,
    privacyUrl: raw.privacy_url ?? null,
    embedOrigins: Array.isArray(raw.embed_origins) ? raw.embed_origins : [],
    modules: { lorawan: raw.modules?.lorawan === true },
    gebiete: Array.isArray(raw.gebiete) ? raw.gebiete : [],
    gebieteAttribution: raw.gebiete_attribution ?? null,
    loaded: true,
    named: Boolean(raw.name?.trim()),
  };
}

/**
 * Aktueller Stand. Bleibt dasselbe Objekt (Inhalt wird ersetzt), damit Module, die `tenant`
 * importiert haben, nach dem Laden den neuen Stand sehen. React-Komponenten nehmen `useTenant()`.
 */
export const tenant: TenantConfig = { ...NEUTRAL_TENANT };

let version = 0;
const listeners = new Set<() => void>();

export function setTenant(next: TenantConfig): void {
  Object.assign(tenant, next);
  version += 1;
  applyTenantToDocument(tenant);
  for (const l of listeners) l();
}

/** Laedt den Stand vom Katalog; bei Fehler bleibt die neutrale Darstellung. */
export async function loadTenant(url = '/api/catalog/tenant-public', fetchImpl: typeof fetch = fetch): Promise<TenantConfig> {
  try {
    const res = await fetchImpl(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(String(res.status));
    setTenant(tenantFromApi((await res.json()) as TenantApi));
  } catch {
    setTenant({ ...NEUTRAL_TENANT });
  }
  return tenant;
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Stand der Kommune; rendert neu, wenn er sich aendert (z. B. nach dem Speichern in der Verwaltung). */
export function useTenant(): TenantConfig {
  useSyncExternalStore(subscribe, () => version);
  return tenant;
}

/**
 * Wendet `brandHue` / `brandChroma` auf das `<html>`-Element an, damit die Custom-Properties aus
 * `styles.css` ueberschrieben werden.
 */
export function applyTenantToDocument(config: TenantConfig = tenant): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--brand-hue', String(config.brandHue));
  root.style.setProperty('--brand-chroma', String(config.brandChroma));
}

/** Grenzen der Gemeinden als GeoJSON (Linien und Namen im Zwilling). */
export function gebieteGeoJson(gebiete: TenantGebiet[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: gebiete.map((g) => ({
      type: 'Feature',
      properties: { name: g.name, ags: g.ags },
      geometry: g.geometry as GeoJSON.Geometry,
    })),
  };
}
